import { useState, useCallback } from 'react';

/**
 * Custom hook for managing async operations
 * Provides loading, error, and success state management
 * 
 * @returns {object} { loading, error, success, execute, reset }
 */
const useAsyncOperation = () => {
  const [state, setState] = useState({
    loading: false,
    error: '',
    success: ''
  });

  /**
   * Execute an async operation with automatic state management
   * @param {function} asyncFn - Async function to execute
   * @param {object} options - Options for execution
   * @param {string} options.successMessage - Custom success message
   * @param {boolean} options.throwOnError - Whether to throw error after setting state
   */
  const execute = useCallback(async (asyncFn, options = {}) => {
    const { successMessage, throwOnError = false } = options;
    
    setState({ loading: true, error: '', success: '' });
    
    try {
      const result = await asyncFn();
      const message = successMessage || 'Operation completed successfully';
      setState({ loading: false, error: '', success: message });
      return result;
    } catch (err) {
      const errorMessage = err?.response?.data?.message || 
                          err?.message || 
                          'An unexpected error occurred';
      setState({ loading: false, error: errorMessage, success: '' });
      
      if (throwOnError) {
        throw err;
      }
      
      return null;
    }
  }, []);

  /**
   * Reset all state
   */
  const reset = useCallback(() => {
    setState({ loading: false, error: '', success: '' });
  }, []);

  /**
   * Set error manually
   */
  const setError = useCallback((error) => {
    setState(prev => ({ ...prev, error, loading: false }));
  }, []);

  /**
   * Set success manually
   */
  const setSuccess = useCallback((success) => {
    setState(prev => ({ ...prev, success, loading: false }));
  }, []);

  return {
    loading: state.loading,
    error: state.error,
    success: state.success,
    execute,
    reset,
    setError,
    setSuccess
  };
};

export default useAsyncOperation;



